const { fetchJSON } = window.BAO.Utils

export class StoreSelector extends window.BAO.CustomElement() {
  static get requiredElements () {
    return [
      /* Stay on store button */
      'stay',
      /* Change store button */
      'move',
      /* Country code input */
      'input',
    ]
  }

  static get requiredAttributes () {
    return [
      /* The current store's country code */
      'data-current-store-country-code',
    ]
  }

  connectedCallback () {
    super.connectedCallback()

    if (!this?.els?.input?.exists) return

    // eslint-disable-next-line compat/compat
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    })

    if (params?.country) {
      return this.setStorageFromURL(params.country)
    }

    if (Shopify.designMode) {
      return
    }

    fetchJSON(`/browsing_context_suggestions.json`, {
      method: 'GET',
    }).then(res => {
      this.setupCountrySwitcherModal(res.detected_values.country.handle)
    })
  }

  setupListeners () {
    super.setupListeners()

    if (this?.els?.move?.exists) {
      this.els.move.elements.forEach(moveEl => {
        this.listeners.add(moveEl, 'click', this.onMoveClick.bind(this))
      })
    }

    if (this?.els?.stay?.exists) {
      this.els.stay.elements.forEach(stayEl => {
        this.listeners.add(stayEl, 'click', this.onStayClick.bind(this))
      })
    }

    if (this?.els?.close?.exists) {
      this.els.close.elements.forEach(closeEl => {
        this.listeners.add(closeEl, 'click', this.onStayClick.bind(this))
      })
    }

    this.listeners.add(
      document,
      'AFTER_SHIPPING_SELECT_CHANGE',
      this.afterShippingSelectChange.bind(this),
    )
  }

  setStorageFromURL (countryCode) {
    this.preferredCountryCode = countryCode
  }

  onMoveClick (event) {
    this.preferredCountryCode = event.target.dataset.countryCode
  }

  onStayClick (event) {
    event.preventDefault()
    this.preferredCountryCode = this.currentStoreCountryCode

    this.setAttribute('aria-hidden', 'true')
  }

  setupCountrySwitcherModal (detectedCountryCode) {
    if (
      detectedCountryCode === this.preferredCountryCode ||
      detectedCountryCode === this.detectedCountryCode
    ) {
      if (this.currentStoreCountryCode === this.preferredCountryCode) {
        return
      }
    }

    this.detectedCountryCode = detectedCountryCode

    if (
      detectedCountryCode !== this.currentStoreCountryCode &&
      this?.sites?.[detectedCountryCode] !== undefined
    ) {
      this.log(`${detectedCountryCode} detected`)
      this.populateForm(detectedCountryCode)
      this.setAttribute('aria-hidden', 'false')
      return
    }

    if (
      this.preferredCountryCode !== this.currentStoreCountryCode &&
      this?.sites?.[this.preferredCountryCode] !== undefined
    ) {
      this.log(`${this.preferredCountryCode} preferred`)
      this.populateForm(this.preferredCountryCode)
      this.setAttribute('aria-hidden', 'false')
    }
  }

  populateForm (countryCode) {
    this.setupElement('title', this.currentStoreCountryCode)
    this.setupElement('move', countryCode, true)
    this.setupElement('stay', this.currentStoreCountryCode, true)
  }

  setupElement (elementName, countryCode, setCountryCode = false) {
    if (this?.els?.[elementName]?.exists) {
      this.els[elementName].elements.forEach(el => {
        el.textContent = el.dataset.textContent.replace(
          '[[Name]]',
          this.sites[countryCode].name,
        )

        if (setCountryCode) {
          el.setAttribute('data-country-code', countryCode)
        }
      })
    }
  }

  afterShippingSelectChange (event) {
    if (!event?.detail?.countryCode) return
    this.preferredCountryCode = event.detail.countryCode
  }

  set detectedCountryCode (countryCode) {
    this._detectedCountryCode = countryCode
    window.localStorage.setItem(this.detectedCountryCodeStorageKey, countryCode)
  }

  get detectedCountryCode () {
    return (
      window.localStorage.getItem(this.detectedCountryCodeStorageKey) ||
      this._detectedCountryCode
    )
  }

  set preferredCountryCode (countryCode) {
    this._preferredCountryCode = countryCode
    this.els.input.element.value = countryCode
    window.localStorage.setItem(
      this.preferredCountryCodeStorageKey,
      countryCode,
    )
  }

  get preferredCountryCode () {
    return (
      window.localStorage.getItem(this.preferredCountryCodeStorageKey) ||
      this._preferredCountryCode
    )
  }

  get currentStoreCountryCode () {
    return this.dataset.currentStoreCountryCode
  }

  get detectedCountryCodeStorageKey () {
    return `BAO-detected-country-code`
  }

  get preferredCountryCodeStorageKey () {
    return `BAO-preferred-country-code`
  }

  get sites () {
    return this.els?.sites?.exists
      ? JSON.parse(this.els.sites.element.textContent)
      : {}
  }
}

if (!customElements.get('store-selector')) {
  customElements.define('store-selector', StoreSelector)
}
